import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { ReviewsList } from '../components/ReviewSystem';
import { AddReviewModal } from '../components/AddReviewModal';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useService, useServices } from '../hooks/useFirestoreServices';
import type { ServiceAddon } from '../hooks/useFirestoreServices';
import LoadingSpinner from '../components/LoadingSpinner';
import { generateReviews, getReviewStats } from '../utils/reviewGenerator';
import { 
  Star, 
  Clock, 
  MapPin, 
  Plus, 
  Minus, 
  Shield, 
  Award, 
  Users, 
  Phone,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Package,
  Calendar
} from 'lucide-react';

interface BookingDetails {
  serviceId: string;
  quantity: number;
  selectedDate: string;
  selectedTime: string;
  selectedAddons: string[];
  totalAmount: number;
}

export default function ServiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { addToCart } = useCart();
  
  const { service: firestoreService, loading: serviceLoading } = useService(id);
  const { services: allServices, loading: allServicesLoading } = useServices();
  
  const [selectedAddons, setSelectedAddons] = useState<string[]>([]);
  const [quantity, setQuantity] = useState(1);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteFormData, setQuoteFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);

  // Use Firestore service data directly
  const service = firestoreService;
  const addons = service?.addons || [];

  // Generate reviews for this service
  const generatedReviews = useMemo(() => {
    if (!service?.id) return [];
    return generateReviews(service.id);
  }, [service?.id]);

  const reviewStats = useMemo(() => {
    if (generatedReviews.length === 0) return null;
    return getReviewStats(generatedReviews);
  }, [generatedReviews]);

  // Get similar services from the same category
  const similarServices = useMemo(() => {
    if (!firestoreService || allServicesLoading) return [];
    
    return allServices
      .filter(s => 
        s.categoryId === firestoreService.categoryId && 
        s.id !== firestoreService.id
      )
      .slice(0, 4);
  }, [firestoreService, allServices, allServicesLoading]);

  // Restore booking details if redirected from login
  useEffect(() => {
    const savedDetails = sessionStorage.getItem('pendingBooking');
    if (savedDetails) {
      const details: BookingDetails = JSON.parse(savedDetails);
      if (details.serviceId === id) {
        setQuantity(details.quantity);
        setSelectedDate(details.selectedDate);
        setSelectedTime(details.selectedTime);
        setSelectedAddons(details.selectedAddons);
      }
      sessionStorage.removeItem('pendingBooking');
    }
  }, [id]);

  const handleAddonToggle = (addonId: string) => {
    setSelectedAddons(prev => 
      prev.includes(addonId) 
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId]
    );
  };

  const calculateTotal = () => {
    if (!service) return 0;
    let total = service.price * quantity;
    selectedAddons.forEach(addonId => {
      const addon = addons.find(a => a.id === addonId);
      if (addon) total += addon.price * quantity;
    });
    return total;
  };

  const calculateDuration = () => {
    if (!service) return '0 min';
    
    // Check if duration contains time units (min/hour) or is a package/travel duration (days/nights)
    const durationStr = service.duration.toLowerCase();
    
    // If duration contains 'day', 'night', or doesn't contain 'min'/'hour', return as-is
    if (durationStr.includes('day') || durationStr.includes('night') || 
        (!durationStr.includes('min') && !durationStr.includes('hour'))) {
      return service.duration; // Return original duration for packages/travel
    }
    
    // For time-based services, calculate total minutes
    let totalMinutes = parseInt(service.duration);
    selectedAddons.forEach(addonId => {
      const addon = addons.find(a => a.id === addonId);
      if (addon?.duration) {
        totalMinutes += parseInt(addon.duration);
      }
    });
    return `${totalMinutes} min`;
  };

  const [showValidationToast, setShowValidationToast] = useState(false);

  const validateBooking = () => {
    if (!service) {
      throw new Error('Service not found');
    }

    if (!selectedDate || !selectedTime) {
      throw new Error('Please select both date and time for your appointment');
    }

    // Validate date is not in the past
    const selectedDateTime = new Date(`${selectedDate}T${selectedTime}`);
    const now = new Date();
    if (selectedDateTime < now) {
      throw new Error('Please select a future date and time');
    }

    // Validate quantity
    if (quantity < 1) {
      throw new Error('Quantity must be at least 1');
    }

    return true;
  };

  const handleBooking = async (type: 'book' | 'cart' | 'checkout') => {
    try {
      validateBooking();
    } catch (error) {
      setToastType('error');
      setToastMessage(error instanceof Error ? error.message : 'Validation failed');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      return;
    }

    if (!user) {
      // Save booking details
      const bookingDetails: BookingDetails = {
        serviceId: id!,
        quantity,
        selectedDate,
        selectedTime,
        selectedAddons,
        totalAmount: calculateTotal()
      };
      sessionStorage.setItem('pendingBooking', JSON.stringify(bookingDetails));
      
      // Redirect to login with return path
      const returnPath = location.pathname;
      navigate('/login', { state: { returnPath } });
      return;
    }

    // Check profile completeness before booking (only for checkout/book actions)
    if (type === 'checkout' || type === 'book') {
      try {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const missingFields = [];
          
          // Check required fields (excluding profile image)
          if (!userData.fullName) missingFields.push('Full Name');
          if (!userData.phoneNumber) missingFields.push('Phone Number');
          if (!userData.email) missingFields.push('Email Address');
          
          const hasAddress = userData.addresses && userData.addresses.length > 0;
          if (!hasAddress) missingFields.push('Delivery Address');
          
          if (missingFields.length > 0) {
            setToastType('error');
            setToastMessage(`Please complete your profile. Missing: ${missingFields.join(', ')}`);
            setShowToast(true);
            setTimeout(() => {
              setShowToast(false);
              navigate('/account?tab=profile');
            }, 2000);
            return;
          }
        }
      } catch (err) {
        console.error('Error checking profile:', err);
      }
    }

    // Handle authenticated booking
    const selectedAddonItems = selectedAddons
      .map(id => addons.find(a => a.id === id))
      .filter((addon): addon is ServiceAddon => addon !== undefined);

    // Service is validated in validateBooking()
    const cartItem = {
      id: service!.id,
      name: service!.name,
      price: service!.price,
      originalPrice: service!.originalPrice,
      image: service!.images?.[0] || service!.image,
      quantity,
      date: selectedDate,
      time: selectedTime,
      duration: calculateDuration(),
      provider: service!.provider || 'RAPSAPP',
      addons: selectedAddonItems.map(addon => ({
        id: addon.id,
        name: addon.name,
        price: addon.price,
        duration: addon.duration
      }))
    };

    if (type === 'cart') {
      // Add to cart using context
      addToCart(cartItem);
      
      // Show success message
      setToastType('success');
      setToastMessage('Service added to cart successfully!');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      
      // Don't redirect, just stay on the page
    } else if (type === 'checkout' || type === 'book') {
      // Add to cart first
      addToCart(cartItem);
      
      // Redirect to cart page instead of checkout
      navigate('/cart');
    }
  };

  const handleProviderContact = async () => {
    if (!service?.provider) {
      setToastType('error');
      setToastMessage('Provider details not available');
      setShowToast(true);
    } else if (!user) {
      const returnPath = location.pathname;
      navigate('/login', { state: { returnPath } });
      return;
    } else {
      try {
        const providerDoc = await getDoc(doc(db, 'providers', service.provider));
        if (providerDoc.exists()) {
          const providerData = providerDoc.data();
          setToastType('success');
          setToastMessage('Connecting with provider...');
          setShowToast(true);
          // Here you can implement chat/call functionality
        } else {
          setToastType('error');
          setToastMessage('Provider not available at the moment');
          setShowToast(true);
        }
      } catch (error) {
        setToastType('error');
        setToastMessage('Error connecting with provider');
        setShowToast(true);
      }
    }
    
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };

  const handleQuoteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!service) return;
    
    // Validation
    if (!quoteFormData.name || !quoteFormData.email || !quoteFormData.phone) {
      setToastType('error');
      setToastMessage('Please fill all required fields');
      setShowToast(true);
      return;
    }

    setQuoteSubmitting(true);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          access_key: 'f01dd54f-e65b-41ca-9a74-f7c15ccb08ec',
          subject: `Quote Request for ${service.name}`,
          from_name: quoteFormData.name,
          name: quoteFormData.name,
          email: quoteFormData.email,
          phone: quoteFormData.phone,
          service_name: service.name,
          service_category: service.category,
          service_price: `₹${service.price}`,
          message: quoteFormData.message || 'No additional message provided',
          quote_request: true
        })
      });

      const data = await response.json();

      if (data.success) {
        setToastType('success');
        setToastMessage('Quote request sent successfully! We will contact you soon.');
        setShowToast(true);
        setShowQuoteModal(false);
        setQuoteFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        throw new Error('Failed to send quote request');
      }
    } catch (error) {
      console.error('Quote request error:', error);
      setToastType('error');
      setToastMessage('Failed to send quote request. Please try again.');
      setShowToast(true);
    } finally {
      setQuoteSubmitting(false);
    }
  };

  const handleQuoteFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setQuoteFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Loading State */}
      {(serviceLoading || allServicesLoading) && (
        <div className="min-h-screen flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col items-center space-y-4"
          >
            <LoadingSpinner />
            <p className="text-gray-600 text-sm md:text-base animate-pulse">
              Loading service details...
            </p>
          </motion.div>
        </div>
      )}

      {/* Error State - Service Not Found */}
      {!serviceLoading && !allServicesLoading && !service && (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Service Not Found</h2>
            <p className="text-gray-600 mb-4">The service you're looking for doesn't exist.</p>
          </div>
        </div>
      )}

      {/* Main Content - Only show when service is loaded */}
      {!serviceLoading && !allServicesLoading && service && (
        <>
          {/* Toast Notification */}
          {showToast && (
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              className="fixed top-4 right-4 z-50"
            >
              <div className={`flex items-center space-x-2 px-4 py-3 rounded-lg shadow-lg ${
                toastType === 'error' 
                  ? 'bg-red-50 border-2 border-red-200' 
                  : 'bg-green-50'
              }`}>
                {toastType === 'error' ? (
                  <AlertCircle className="h-5 w-5 text-red-500" />
                ) : (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                )}
                <span className={`font-medium ${
                  toastType === 'error' ? 'text-red-800' : 'text-green-800'
                }`}>{toastMessage}</span>
              </div>
            </motion.div>
          )}

      <div className="max-w-7xl mx-auto px-3 md:px-4 py-4 md:py-8">
        {/* Mobile: Service Info First, then Booking, then Similar Services & Reviews */}
        {/* Desktop: Service Info & Similar/Reviews on left, Booking on right */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
          {/* Left Column - Service Info, Similar Services, Reviews */}
          <div className="lg:col-span-2 order-1 lg:order-1">
                        <div className="bg-white rounded-xl md:rounded-2xl shadow-sm overflow-hidden mb-4 md:mb-8">
              <img
                src={service.images?.[0] || service.image}
                alt={service.name}
                className="w-full h-48 sm:h-64 md:h-80 lg:h-96 object-cover"
              />
            </div>

            <div className="bg-white rounded-xl md:rounded-2xl shadow-sm p-4 md:p-6">
              <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-4 md:mb-6 space-y-3 md:space-y-0">
                <div className="flex-1">
                  {/* Category Badge */}
                  {service.category && (
                    <div className="mb-2 md:mb-3">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs md:text-sm font-semibold bg-gradient-to-r from-primary-100 to-blue-100 text-primary-700 border border-primary-200 shadow-sm">
                        <Package className="h-3.5 w-3.5 md:h-4 md:w-4 mr-1.5" />
                        {service.category}
                      </span>
                    </div>
                  )}
                  <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-2 md:mb-3">{service.name}</h1>
                  <div className="flex flex-wrap items-center gap-2 md:gap-3 lg:gap-4 text-xs md:text-sm text-gray-600">
                    <div className="flex items-center space-x-1">
                      <Star className="h-3.5 w-3.5 md:h-4 md:w-4 text-yellow-400 fill-current" />
                      <span>{reviewStats?.avgRating.toFixed(1) || '5.0'}</span>
                      <span className="text-gray-500">({reviewStats?.totalReviews || 0})</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="h-3.5 w-3.5 md:h-4 md:w-4" />
                      <span>{service.duration}</span>
                    </div>
                    {service.location && (
                      <div className="flex items-center space-x-1">
                        <MapPin className="h-3.5 w-3.5 md:h-4 md:w-4" />
                        <span className="truncate max-w-[150px] sm:max-w-none">{service.location}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-start md:items-end bg-primary-50 md:bg-transparent p-3 md:p-0 rounded-lg md:rounded-none -mx-4 md:mx-0">
                  <div className="flex flex-col md:items-end">
                    <div className="text-xl md:text-2xl lg:text-3xl font-bold text-primary-600">
                      ₹{(service.price || 0).toLocaleString('en-IN')}
                    </div>
                    {service.originalPrice && service.originalPrice > (service.price || 0) && (
                      <div className="flex items-center space-x-2">
                        <span className="text-sm md:text-base lg:text-lg text-gray-400 line-through">
                          ₹{service.originalPrice.toLocaleString('en-IN')}
                        </span>
                        <span className="text-xs md:text-sm px-2 py-0.5 bg-green-100 text-green-800 rounded-full font-medium">
                          {Math.round(((service.originalPrice - (service.price || 0)) / service.originalPrice) * 100)}% OFF
                        </span>
                      </div>
                    )}
                    <div className="text-xs text-gray-500 mt-1">
                      {service.duration} • Per Service
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-b border-gray-200 mb-4 md:mb-6">
                <nav className="-mb-px flex space-x-4 md:space-x-8">
                  {[
                    { id: 'overview', name: 'Overview' },
                    { id: 'details', name: 'What\'s Included' }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`py-2 px-1 border-b-2 font-medium text-xs md:text-sm ${
                        activeTab === tab.id
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab.name}
                    </button>
                  ))}
                </nav>
              </div>

              {activeTab === 'overview' && (
                <div className="space-y-4 md:space-y-6">
                  <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                    {service.longDescription || service.description}
                  </p>
                  
                  {Array.isArray(service.features) && service.features.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2 md:mb-3">Key Features</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
                        {service.features.map((feature, index) => (
                          <motion.div
                            key={feature}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.2, delay: index * 0.1 }}
                            className="flex items-center space-x-2 bg-gray-50 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-green-500 flex-shrink-0" />
                            <span className="text-gray-700 text-sm md:text-base">{feature}</span>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  <div className="flex flex-wrap items-center gap-3 md:gap-4 pt-3 md:pt-4 border-t">
                    <div className="flex items-center space-x-1.5 md:space-x-2">
                      <Shield className="h-4 w-4 md:h-5 md:w-5 text-primary-500" />
                      <span className="text-xs md:text-sm text-gray-600">100% Guaranteed</span>
                    </div>
                    <div className="flex items-center space-x-1.5 md:space-x-2">
                      <Award className="h-4 w-4 md:h-5 md:w-5 text-purple-500" />
                      <span className="text-xs md:text-sm text-gray-600">Certified Pro</span>
                    </div>
                    <div className="flex items-center space-x-1.5 md:space-x-2">
                      <Users className="h-4 w-4 md:h-5 md:w-5 text-green-500" />
                      <span className="text-xs md:text-sm text-gray-600">{service.reviewCount || 0}+ Happy Customers</span>
                    </div>
                  </div>

                  {/* Request Quote Section */}
                  <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-gray-200">
                    <div className="bg-gradient-to-r from-secondary-50 to-purple-50 rounded-lg md:rounded-xl p-4 md:p-6">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
                        <div className="flex-1">
                          <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1 md:mb-2">Need a Custom Quote?</h3>
                          <p className="text-xs md:text-sm text-gray-600 leading-relaxed">Get a personalized quote based on your requirements</p>
                        </div>
                        <button
                          onClick={() => setShowQuoteModal(true)}
                          className="flex items-center justify-center space-x-2 px-4 md:px-5 py-2.5 md:py-3 bg-gradient-to-r from-secondary-500 to-secondary-600 text-white rounded-lg hover:from-secondary-600 hover:to-secondary-700 transition-all duration-300 shadow-md hover:shadow-lg font-medium text-sm md:text-base whitespace-nowrap"
                        >
                          <MessageSquare className="h-4 w-4 md:h-5 md:w-5" />
                          <span>Customize Package</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'details' && (
                <div>
                  <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">What's Included</h3>
                  {service.includes && service.includes.length > 0 ? (
                    <div className="space-y-2 md:space-y-3">
                      {service.includes.map((item, index) => (
                        <div key={index} className="flex items-center space-x-2 md:space-x-3">
                          <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-green-500 flex-shrink-0" />
                          <span className="text-gray-700 text-sm md:text-base">{item}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-600 text-sm">No additional details available.</p>
                  )}
                </div>
              )}
            </div>

            {/* Similar Services Section - Hidden on mobile, shown on desktop */}
            <div className="hidden lg:block bg-white rounded-xl shadow-sm p-6 md:p-8 mt-6 lg:mt-8">
              <div className="mb-6 pb-6 border-b border-gray-200">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Similar Services</h2>
                <p className="text-sm text-gray-500 mt-1">You may also like these {service.category} services</p>
              </div>

              {similarServices.length > 0 ? (
                <div className="space-y-4">
                  {similarServices.map((similarService) => (
                    <motion.div
                      key={similarService.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="bg-gradient-to-br from-gray-50 to-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg hover:border-primary-200 transition-all duration-300 group"
                    >
                      <div className="flex gap-4 p-4">
                        {/* Service Image */}
                        <div className="relative w-32 h-32 flex-shrink-0 rounded-lg overflow-hidden">
                          <img
                            src={similarService.images?.[0] || similarService.image}
                            alt={similarService.name}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                          {similarService.discount && (
                            <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-0.5 rounded-full text-xs font-bold shadow-md">
                              {similarService.discount}% OFF
                            </div>
                          )}
                        </div>

                        {/* Service Info */}
                        <div className="flex-1 flex flex-col justify-between min-w-0">
                          <div>
                            <h3 className="font-bold text-gray-900 text-base mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                              {similarService.name}
                            </h3>
                            
                            <div className="flex items-center gap-3 mb-2">
                              <div className="flex items-center gap-1">
                                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                                <span className="text-sm font-semibold text-gray-900">{similarService.rating}</span>
                              </div>
                              <span className="text-xs text-gray-500">({similarService.reviewCount})</span>
                            </div>

                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Clock className="h-4 w-4 text-primary-600" />
                              <span className="font-medium">{similarService.duration}</span>
                            </div>
                          </div>

                          <div className="flex items-center justify-between pt-2 mt-2 border-t border-gray-100">
                            <div className="flex items-baseline gap-2">
                              <span className="text-xl font-bold text-primary-600">
                                ₹{similarService.discount 
                                  ? Math.round(similarService.price * (1 - similarService.discount / 100))
                                  : similarService.price}
                              </span>
                              {similarService.discount && (
                                <span className="text-sm text-gray-400 line-through">
                                  ₹{similarService.price}
                                </span>
                              )}
                            </div>
                            <button 
                              onClick={() => navigate(`/services/${similarService.id}`)}
                              className="px-4 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium shadow-sm hover:shadow-md"
                            >
                              View
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No similar services found</p>
              )}
            </div>

            {/* Customer Reviews Section - Hidden on mobile, shown on desktop */}
            <div className="hidden lg:block bg-white rounded-xl shadow-sm p-6 md:p-8 mt-6 lg:mt-8">
              {/* Header with Rating Summary */}
              <div className="flex flex-col lg:flex-row justify-between items-start gap-6 mb-8 pb-6 border-b border-gray-200">
                <div className="flex-1">
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Customer Reviews</h2>
                  <div className="flex items-center gap-4 mt-3">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`h-5 w-5 ${
                              star <= Math.round(reviewStats?.avgRating || 5)
                                ? 'text-yellow-400 fill-current'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-2xl font-bold text-gray-900">
                        {reviewStats?.avgRating.toFixed(1) || '5.0'}
                      </span>
                    </div>
                    <div className="h-6 w-px bg-gray-300"></div>
                    <p className="text-gray-600">
                      Based on <span className="font-semibold text-gray-900">{reviewStats?.totalReviews || 0}</span> reviews
                    </p>
                  </div>
                </div>
                {user && (
                  <button
                    onClick={() => setShowReviewModal(true)}
                    className="px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-all flex items-center gap-2 font-medium shadow-sm hover:shadow-md whitespace-nowrap"
                  >
                    <Star className="h-4 w-4" />
                    Write a Review
                  </button>
                )}
              </div>

              {/* Reviews List */}
              <div className="max-h-[600px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                {generatedReviews.length > 0 && (
                  <div className="space-y-6">
                    {generatedReviews.slice(0, 5).map((review) => (
                      <div key={review.id} className="border-b border-gray-100 last:border-0 pb-6 last:pb-0">
                        <div className="flex items-start gap-4">
                          {review.userAvatar ? (
                            <img 
                              src={review.userAvatar} 
                              alt={review.userName}
                              className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                              <span className="text-gray-600 font-semibold text-lg">
                                {review.userName.charAt(0)}
                              </span>
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <h4 className="font-semibold text-gray-900">{review.userName}</h4>
                                <p className="text-sm text-gray-500">{review.location}</p>
                              </div>
                              <span className="text-xs text-gray-400">{review.date}</span>
                            </div>
                            <div className="flex items-center gap-1 mb-2">
                              {[...Array(5)].map((_, i) => (
                                <Star 
                                  key={i} 
                                  className={`h-4 w-4 ${
                                    i < review.rating 
                                      ? 'fill-yellow-400 text-yellow-400' 
                                      : 'text-gray-300'
                                  }`} 
                                />
                              ))}
                            </div>
                            <p className="text-gray-700">{review.comment}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Booking Sidebar (Order 2 on mobile, shows after service info) */}
          <div className="lg:col-span-1 order-2 lg:order-2">
            <div className="bg-white rounded-xl md:rounded-2xl shadow-sm p-4 md:p-6 lg:p-8">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">Book This Service</h2>
              
              {!user ? (
                <div className="text-center py-4 md:py-6">
                  <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2 md:mb-3">Please Login First</h3>
                  <p className="text-sm md:text-base text-gray-600 mb-3 md:mb-4">You need to be logged in to book this service</p>
                  <Link
                    to="/login"
                    state={{ returnPath: location.pathname }}
                    className="inline-block bg-primary-600 text-white px-5 py-2 md:px-6 md:py-2.5 rounded-lg hover:bg-primary-700 transition-colors text-sm md:text-base"
                  >
                    Login / Sign Up
                  </Link>
                </div>
              ) : (
                <>
                {/* Number of People */}
                <div className="mb-4 md:mb-6">
                  <label className="block text-sm md:text-base font-semibold text-primary-700 mb-2 md:mb-3">
                    Number of People
                  </label>
                <div className="flex items-center space-x-3 md:space-x-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-2 border border-gray-300 rounded-lg hover:border-primary-300"
                  >
                    <Minus className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  </button>
                  <span className="text-base md:text-lg font-semibold px-3 md:px-4">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-2 border border-gray-300 rounded-lg hover:border-primary-300"
                  >
                    <Plus className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  </button>
                </div>
              </div>

              {/* Date Selection */}
              <div className="mb-4 md:mb-6">
                <label className="block text-sm md:text-base font-semibold text-primary-700 mb-2 md:mb-3">
                  Select Date *
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    required
                    className="w-full px-3 py-3 md:px-4 md:py-3.5 pr-3 md:pr-4 border-2 border-primary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 bg-white cursor-pointer text-sm md:text-base font-semibold"
                    style={{
                      colorScheme: 'light'
                    }}
                    onFocus={(e) => {
                      // Show date picker on mobile/tablet
                      e.target.showPicker?.();
                    }}
                  />
                </div>
                {!selectedDate && (
                  <p className="text-xs text-gray-500 mt-1">Please select your preferred service date</p>
                )}
              </div>

              {/* Time Selection */}
              <div className="mb-4 md:mb-6">
                <label className="block text-sm md:text-base font-semibold text-primary-700 mb-2 md:mb-3">
                  Select Time *
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-primary-600 pointer-events-none z-10" />
                  <input
                    type="time"
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                    required
                    min="08:00"
                    max="20:00"
                    step="900"
                    className="w-full px-3 py-3 md:px-4 md:py-3.5 pl-10 md:pl-12 border-2 border-primary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 bg-white cursor-pointer text-base md:text-lg font-medium hover:border-primary-400 transition-colors"
                    style={{
                      colorScheme: 'light'
                    }}
                  />
                </div>
                <div className="mt-2 flex items-start gap-2">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-xs text-gray-600">
                    <span className="font-semibold text-gray-700">Select time:</span> Click the field and choose from the time picker, or type directly (e.g., 14:30 for 2:30 PM). Available <span className="font-semibold">8:00 AM - 8:00 PM</span> in 15-min intervals.
                  </p>
                </div>
              </div>
              </>
              )}

              {user && (
              <>
              {/* Price Breakdown */}
              <div className="border-t pt-4 mb-6">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Service ({quantity}x)</span>
                    <span>₹{service.price * quantity}</span>
                  </div>
                  
                  {selectedAddons.map(addonId => {
                    const addon = addons.find(a => a.id === addonId);
                    return addon ? (
                      <div key={addonId} className="flex justify-between text-gray-600">
                        <span>{addon.name} ({quantity}x)</span>
                        <span>₹{addon.price * quantity}</span>
                      </div>
                    ) : null;
                  })}
                </div>
                
                <div className="border-t mt-4 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold">Total</span>
                    <span className="text-2xl font-bold text-primary-600">₹{calculateTotal()}</span>
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    Duration: {calculateDuration()}
                  </div>
                </div>
              </div>

              {/* Available Add-ons Section */}
              {addons && addons.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Package className="h-5 w-5 text-primary-600" />
                    <h3 className="text-lg font-bold text-gray-900">Available Add-ons</h3>
                  </div>
                  
                  <div className="space-y-3 max-h-[400px] overflow-y-auto scrollbar-thin">
                    {addons.map(addon => (
                      <div
                        key={addon.id}
                        className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                          selectedAddons.includes(addon.id)
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-200 hover:border-primary-300'
                        }`}
                        onClick={() => handleAddonToggle(addon.id)}
                      >
                        <div className="flex justify-between items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <h4 className="font-semibold text-gray-900 text-sm">{addon.name}</h4>
                              {addon.popular && (
                                <span className="text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full flex-shrink-0">
                                  Popular
                                </span>
                              )}
                            </div>
                            <p className="text-gray-600 text-xs mb-2 line-clamp-2">{addon.description}</p>
                            <div className="flex items-center space-x-3 text-xs text-gray-500">
                              <span className="font-semibold text-primary-600">₹{addon.price}</span>
                              {addon.duration && (
                                <span className="flex items-center space-x-1">
                                  <Clock className="h-3 w-3" />
                                  <span>{addon.duration}</span>
                                </span>
                              )}
                            </div>
                          </div>
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            selectedAddons.includes(addon.id)
                              ? 'border-primary-500 bg-primary-500'
                              : 'border-gray-300'
                          }`}>
                            {selectedAddons.includes(addon.id) && (
                              <CheckCircle className="h-3.5 w-3.5 text-white" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Booking Actions */}
              <div className="space-y-3">
                <button 
                  onClick={() => handleBooking('checkout')}
                  className="w-full bg-primary-600 text-white py-3 rounded-lg hover:bg-primary-700 transition-colors font-semibold"
                >
                  Buy Now
                </button>
                <button 
                  onClick={() => handleBooking('cart')}
                  className="w-full bg-white text-primary-600 border-2 border-primary-600 py-3 rounded-lg hover:bg-primary-50 transition-colors font-semibold"
                >
                  Add to Cart
                </button>
              </div>
              </>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Only: Similar Services and Reviews - Show after booking section */}
        <div className="lg:hidden mt-6 space-y-6">
          {/* Similar Services - Mobile */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="mb-6 pb-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Similar Services</h2>
              <p className="text-sm text-gray-500 mt-1">You may also like these {service.category} services</p>
            </div>

            {similarServices.length > 0 ? (
              <div className="space-y-4">
                {similarServices.map((similarService) => (
                  <motion.div
                    key={similarService.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="bg-gradient-to-br from-gray-50 to-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg hover:border-primary-200 transition-all duration-300 group"
                  >
                    <div className="flex gap-4 p-4">
                      {/* Service Image */}
                      <div className="relative w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden">
                        <img
                          src={similarService.images?.[0] || similarService.image}
                          alt={similarService.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                        {similarService.discount && (
                          <div className="absolute top-1 right-1 bg-red-500 text-white px-1.5 py-0.5 rounded-full text-xs font-bold shadow-md">
                            {similarService.discount}% OFF
                          </div>
                        )}
                      </div>

                      {/* Service Info */}
                      <div className="flex-1 flex flex-col justify-between min-w-0">
                        <div>
                          <h3 className="font-bold text-gray-900 text-sm mb-1.5 line-clamp-2 group-hover:text-primary-600 transition-colors">
                            {similarService.name}
                          </h3>
                          
                          <div className="flex items-center gap-2 mb-1.5">
                            <div className="flex items-center gap-1">
                              <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                              <span className="text-xs font-semibold text-gray-900">{similarService.rating}</span>
                            </div>
                            <span className="text-xs text-gray-500">({similarService.reviewCount})</span>
                          </div>

                          <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-2">
                            <Clock className="h-3.5 w-3.5 text-primary-600" />
                            <span className="font-medium">{similarService.duration}</span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                          <div className="flex items-baseline gap-1.5">
                            <span className="text-lg font-bold text-primary-600">
                              ₹{similarService.discount 
                                ? Math.round(similarService.price * (1 - similarService.discount / 100))
                                : similarService.price}
                            </span>
                            {similarService.discount && (
                              <span className="text-xs text-gray-400 line-through">
                                ₹{similarService.price}
                              </span>
                            )}
                          </div>
                          <button 
                            onClick={() => navigate(`/services/${similarService.id}`)}
                            className="px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-xs font-medium shadow-sm"
                          >
                            View
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8 text-sm">No similar services found</p>
            )}
          </div>

          {/* Customer Reviews - Mobile */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex flex-col gap-4 mb-6 pb-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Customer Reviews</h2>
                  <div className="flex items-center gap-3 mt-2">
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`h-4 w-4 ${
                            star <= Math.round(reviewStats?.avgRating || 5)
                              ? 'text-yellow-400 fill-current'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-lg font-bold text-gray-900">
                      {reviewStats?.avgRating.toFixed(1) || '5.0'}
                    </span>
                    <span className="text-sm text-gray-500">({reviewStats?.totalReviews || 0} reviews)</span>
                  </div>
                </div>
              </div>
              {user && (
                <button
                  onClick={() => setShowReviewModal(true)}
                  className="w-full px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-all flex items-center justify-center gap-2 font-medium shadow-sm text-sm"
                >
                  <Star className="h-4 w-4" />
                  Write a Review
                </button>
              )}
            </div>

            <div>
              {generatedReviews.length > 0 && (
                <div className="space-y-6">
                  {generatedReviews.slice(0, 10).map((review) => (
                    <div key={review.id} className="border-b border-gray-100 last:border-0 pb-6 last:pb-0">
                      <div className="flex items-start gap-4">
                        {review.userAvatar ? (
                          <img 
                            src={review.userAvatar} 
                            alt={review.userName}
                            className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                            <span className="text-gray-600 font-semibold text-lg">
                              {review.userName.charAt(0)}
                            </span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-2 gap-2">
                            <div>
                              <h4 className="font-semibold text-gray-900">{review.userName}</h4>
                              <p className="text-sm text-gray-500">{review.location}</p>
                            </div>
                            <span className="text-xs text-gray-400 flex-shrink-0">{review.date}</span>
                          </div>
                          <div className="flex items-center gap-1 mb-2">
                            {[...Array(5)].map((_, i) => (
                              <Star 
                                key={i} 
                                className={`h-4 w-4 ${
                                  i < review.rating 
                                    ? 'fill-yellow-400 text-yellow-400' 
                                    : 'text-gray-300'
                                }`} 
                              />
                            ))}
                          </div>
                          <p className="text-gray-700 text-sm">{review.comment}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Review Modal */}
      {showReviewModal && service && (
        <AddReviewModal
          serviceId={service.id}
          serviceName={service.name}
          onClose={() => setShowReviewModal(false)}
          onSuccess={() => {
            setShowReviewModal(false);
            setToastType('success');
            setToastMessage('Thank you! Your review has been submitted successfully.');
            setShowToast(true);
            setTimeout(() => setShowToast(false), 3000);
          }}
        />
      )}

      {/* Quote Request Modal */}
      {showQuoteModal && service && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-xl md:rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-gradient-to-r from-secondary-500 to-secondary-600 text-white p-4 md:p-6 rounded-t-xl md:rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg md:text-2xl font-bold">Request a Quote</h2>
                  <p className="text-secondary-100 text-xs md:text-sm mt-1">Get a custom quote for {service.name}</p>
                </div>
                <button
                  onClick={() => setShowQuoteModal(false)}
                  className="text-white hover:bg-white/20 rounded-full p-1.5 md:p-2 transition-colors"
                >
                  <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <form onSubmit={handleQuoteSubmit} className="p-4 md:p-6 space-y-3 md:space-y-4">
              {/* Service Info */}
              <div className="bg-gray-50 rounded-lg p-3 md:p-4 mb-3 md:mb-4">
                <div className="flex items-center space-x-2 md:space-x-3">
                  <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={service.images?.[0] || service.image}
                      alt={service.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm md:text-base">{service.name}</h3>
                    <p className="text-xs md:text-sm text-gray-600">{service.category}</p>
                    <p className="text-xs md:text-sm font-semibold text-primary-600">₹{service.price}</p>
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              <div>
                <label htmlFor="quote-name" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="quote-name"
                  name="name"
                  required
                  value={quoteFormData.name}
                  onChange={handleQuoteFormChange}
                  className="w-full px-3 py-2 md:px-4 md:py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
                  placeholder="Enter your name"
                />
              </div>

              <div>
                <label htmlFor="quote-email" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="quote-email"
                  name="email"
                  required
                  value={quoteFormData.email}
                  onChange={handleQuoteFormChange}
                  className="w-full px-3 py-2 md:px-4 md:py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label htmlFor="quote-phone" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="quote-phone"
                  name="phone"
                  required
                  value={quoteFormData.phone}
                  onChange={handleQuoteFormChange}
                  className="w-full px-3 py-2 md:px-4 md:py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
                  placeholder="10-digit mobile number"
                />
              </div>

              <div>
                <label htmlFor="quote-message" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                  Additional Requirements (Optional)
                </label>
                <textarea
                  id="quote-message"
                  name="message"
                  rows={3}
                  value={quoteFormData.message}
                  onChange={handleQuoteFormChange}
                  className="w-full px-3 py-2 md:px-4 md:py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent resize-none"
                  placeholder="Tell us about your specific requirements..."
                />
              </div>

              {/* Submit Button */}
              <div className="flex space-x-2 md:space-x-3 pt-3 md:pt-4">
                <button
                  type="button"
                  onClick={() => setShowQuoteModal(false)}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={quoteSubmitting}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-secondary-500 to-secondary-600 text-white rounded-lg hover:from-secondary-600 hover:to-secondary-700 transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {quoteSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <MessageSquare className="h-5 w-5" />
                      <span>Send Request</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
        </>
      )}
    </div>
  );
}
